import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Button, Grid } from "@mui/material";
import BasicDialog from "../../base/BasicDialog";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import { useAsync } from "../../../hooks/useAsync";
import { useForm } from "../../../hooks/useForm";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCrudFn } from "../../../hooks/useCrudFn";
import useDialog from "../../../hooks/useDialog";
import IsTestingAppWrapper from "../../wrapper/IsTestingAppWrapper/IsTestingAppWrapper";
import FunctionsDrawer, { FUNCTION_DRAWER_TYPE_HRS } from "../../drawer/FunctionsDrawer/FunctionsDrawer";
import { openMiniDrawerWidth } from "../../drawer/MiniDrawer/MiniDrawer";
import BoxWithTypography from "../../box/BoxWithTypography/BoxWithTypography";
import HrSurveyMainDataForm from "../../form/HrSurveyMainDataForm/HrSurveyMainDataForm";
import SurveyScoreDetailsForm from "../../form/SurveyScoreDetailsForm/SurveyScoreDetailsForm";
import SurveyQuestionDetailsForm from "../../form/SurveyQuestionDetailsForm/SurveyQuestionDetailsForm";
import HrParticipantDetailsForm from "../../form/HrParticipantDetailsForm/HrParticipantDetailsForm";
import BaseTable from "../../base/BaseTable/BaseTable";
import UniversalDetailsDialog from "../UniversalDetailsDialog/UniversalDetailsDialog";
import useHRService from "../../../services/hrService";
import TableService from "../../../services/tableService";
import {
  SURVEY_SCORE_TABLE_FIELDS_CONFIG,
  SURVEY_QUESTION_TABLE_FIELDS_CONFIG,
  HR_PARTICIPANT_TABLE_FIELDS_CONFIG,
} from "./DialogTablesConfig";
import { isEmptyValue, getRandomString } from "../../../helpers/methods";
import HrQuestionKindForm from "../../form/HrQuestionKindForm";
import { HR_SURVEY_SEND_ACTION, HR_SURVEY_SEND_TO_ANOTHERS } from "../../../helpers/constants";
import useUserService from "../../../services/userService";
import SnackbarAlert from "../../base/SnackbarAlert";
import { useSnackbarAlert } from "../../../context/snackbarAlert";


const EMPTY_SURVEY_FORM = {
  name: "",
  goal: "",
  invitation_content: "",
  creation_date: null,
  send_date: null,
  reminder1_days: null,
  reminder2_days: null,
  expiration_date: null,
  closing_date: null,
  organizer: null,
  is_in_progress : true
};





const HrSurveyDialog = (props) => {
  const { t } = useTranslation();

  const [localSurveyId, setLocalSurveyId] = useState(props.surveyId);

  const [isSavingData, setIsSavingData] = useState(false);
  const [filteringData, setFilteringData] = useState({});
  const [localScores, setLocalScores] = useState([]);
  const [localQuestions, setLocalQuestions] = useState([]);
  const [localParticipants, setLocalParticipants] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(true && !isEmptyValue(localSurveyId))
  const [isSurveyChanged, setIsSurveyChanged] = useState(false)
  const [isCopyMode, setIsCopyMode] = useState(props.isCopyMode)
  const [copiedSurveyId, setCopiedSurveyId] = useState(props.isCopyMode ? props.surveyId : undefined);
  const [localQuestionKinds, setLocalQuestionKinds] = useState([])

  const {

    formValue: surveyFormValue,
    setFormValue: setSurveyFormValue,
    onChange: onChangeSurveyForm,
    onChangeDate: onChangeSurveyDate,
    onChangeAutocompleteFieldWithObjectOptions: onChangeSurveyAutocomplete,
  } = useForm(EMPTY_SURVEY_FORM);



  const [isFieldChanged, setIsFieldChanged] = useState(false);

  const onChangeSurveyFormTracked = useCallback((e) => {
    setIsFieldChanged(true);
    onChangeSurveyForm(e);
  }, [onChangeSurveyForm]);

  const {
    getSurveyDetails,
    getSurveyFilteringData,
    getSurveyScores,
    getSurveyQuestions,
    getHrParticipants,
    createSurvey,
    updateSurvey,
    createSurveyScore,
    deleteSurveyScore,
    createSurveyQuestion,
    deleteSurveyQuestion,
    createHrParticipant,
    deleteHrParticipant,
    createHrQuestionKind,
    getSurveyQuestionKindList
  } = useHRService();

  const { getUserSelectList } = useUserService();
  const snackbarAlert = useSnackbarAlert();


  const surveyCrudFn = useCrudFn(createSurvey, updateSurvey, null, "survey");
  const scoreCrudFn = useCrudFn(null, null, deleteSurveyScore, "survey_score");
  const questionCrudFn = useCrudFn(null, null, deleteSurveyQuestion, "survey_question");
  const participantCrudFn = useCrudFn(null, null, deleteHrParticipant, "hr_participant");
  const surveyQuestionKindList = useAsync(() => {
    if (localSurveyId) return getSurveyQuestionKindList(localSurveyId)
    return Promise.resolve([])

  }, [localSurveyId]);

  const saveLocal = isEmptyValue(localSurveyId);

  const [openScoreDialog, onOpenScoreDialog, onCloseScoreDialog] = useDialog();
  const [openQuestionDialog, onOpenQuestionDialog, onCloseQuestionDialog] = useDialog();
  const [openParticipantDialog, onOpenParticipantDialog, onCloseParticipantDialog] = useDialog();
  const [openAnotherParticipantDialog, onOpenAnotherParticipantDialog, onCloseAnotherParticipantDialog] = useDialog();
  const [openNewCategoryDialog, onOpenNewCategoryDialog, onCloseNewCategoryDialog] = useDialog();

  const onSaveData = useCallback(
    (data) => {
      setIsSurveyChanged(true);
      return { ...data, hrsurvey: localSurveyId };
    },
    [localSurveyId]
  );

  const onChangeSurveyDateTracked = useCallback((e) => {
    setIsFieldChanged(true);
    let { name, value } = onChangeSurveyDate(e);
    if (!isEmptyValue(localSurveyId)) {
      let dataToSend = {}
      dataToSend[name] = value
      surveyCrudFn.onUpdateObject(localSurveyId, dataToSend)
    }
  }, [localSurveyId, onChangeSurveyDate]);


  const onSaveLocalScore = useCallback((data) => {
    delete data['id']
    setLocalScores((prev) => [...prev, data]);
    onCloseScoreDialog();
  }, [onCloseScoreDialog]);

  const onSaveLocalQuestion = useCallback((data) => {
    delete data['id']
    setLocalQuestions((prev) => [...prev, data]);
    onCloseQuestionDialog();
  }, [onCloseQuestionDialog]);



  const onSaveLocalParticipant = useCallback((data) => {
    delete data['id']
    setLocalParticipants((prev) => [...prev, data]);
    onCloseParticipantDialog();
  }, [onCloseParticipantDialog]);

  const onSaveAnotherParticipant = useCallback((data) => {
    surveyCrudFn.onUpdateObject(localSurveyId, { id: localSurveyId, action: HR_SURVEY_SEND_TO_ANOTHERS, participants: [data] }, hrParticipantsAsync.refetch)
    onCloseAnotherParticipantDialog();
  }, [onCloseParticipantDialog]);

  const expirationMinDate = useMemo(
    () => (new Date()),
    []
  );

  const filteringDataAsync = useAsync(getSurveyFilteringData, []);

  useEffect(() => {
    if (!filteringDataAsync.loading && filteringDataAsync.value) {
      setFilteringData(filteringDataAsync.value);
    }
  }, [filteringDataAsync.loading]);

  useEffect(() => {
    if (!surveyQuestionKindList.loading && surveyQuestionKindList.value) {
      setLocalQuestionKinds(surveyQuestionKindList.value);
    }
  }, [surveyQuestionKindList.loading]);



  const surveyDetailsAsync = useAsync(
    () => (localSurveyId ? getSurveyDetails(localSurveyId) : Promise.resolve(surveyFormValue)),
    [localSurveyId]
  );

  useEffect(() => {
    if (!surveyDetailsAsync.loading && surveyDetailsAsync.value) {

      setIsFieldChanged(false);
      setIsReadOnly(!surveyDetailsAsync.value?.is_in_progress !== false)
      if (isCopyMode) {
        setLocalSurveyId(undefined)
        setSurveyFormValue({ ...surveyDetailsAsync.value, id: undefined, hrsurvey_date_expired: undefined, hrsurvey_date_send: undefined, created: undefined, hrsurvey_date_close: undefined });
        setIsReadOnly(false)
      } else {
        setSurveyFormValue(surveyDetailsAsync.value);
      }
    }
  }, [surveyDetailsAsync.loading]);

  const surveyScoresAsync = useAsync(
    () => (isCopyMode ? getSurveyScores(copiedSurveyId) : localSurveyId ? getSurveyScores(localSurveyId) : Promise.resolve([])),
    [localSurveyId, copiedSurveyId, isCopyMode]
  );
  useEffect(() => {
    if (!surveyScoresAsync.loading && surveyScoresAsync.value) {
      setLocalScores(surveyScoresAsync.value);
    }
  }, [surveyScoresAsync.loading]);

  const surveyQuestionsAsync = useAsync(
    () => (isCopyMode ? getSurveyQuestions(copiedSurveyId) : localSurveyId ? getSurveyQuestions(localSurveyId) : Promise.resolve([])),
    [localSurveyId, copiedSurveyId, isCopyMode]
  );
  useEffect(() => {
    if (!surveyQuestionsAsync.loading && surveyQuestionsAsync.value) {
      setLocalQuestions(surveyQuestionsAsync.value);
    }
  }, [surveyQuestionsAsync.loading]);

  const hrParticipantsAsync = useAsync(
    () => (localSurveyId && !isCopyMode ? getHrParticipants({ survey: localSurveyId, hrrole_is_selfevaluated: false, hrrole_is_organizer: false }) : Promise.resolve([])),
    [localSurveyId, isCopyMode]
  );


  const userSelectList = useAsync(
    () => {
      if (hrParticipantsAsync.loading) return Promise.resolve([])
      let idsToExclude = localParticipants.map((p) => p.hrparticipant) || []
      return getUserSelectList({ 'is_external': false, 'exclude_ids': idsToExclude })
    }, [localParticipants]
  );


  useEffect(() => {
    if (!hrParticipantsAsync.loading && hrParticipantsAsync.value) {
      setLocalParticipants(hrParticipantsAsync.value);
    }
  }, [hrParticipantsAsync.loading]);

  const isLoading =
    filteringDataAsync.loading ||
    surveyDetailsAsync.loading ||
    surveyScoresAsync.loading ||
    surveyQuestionsAsync.loading ||
    hrParticipantsAsync.loading ||
    scoreCrudFn.isDeletingLoading ||
    questionCrudFn.isDeletingLoading ||
    participantCrudFn.isDeletingLoading ||
    userSelectList.loading
    ;

  const handleSurveyFormBlur = useCallback(() => {
    if (isEmptyValue(localSurveyId) || !isFieldChanged) return;
    setIsFieldChanged(false);
    setIsSurveyChanged(true);
    let dataToSend = { ...surveyFormValue }
    delete dataToSend['creator']
    surveyCrudFn.onUpdateObject(localSurveyId, dataToSend);
  }, [localSurveyId, isFieldChanged, surveyFormValue]);

  const participantTableHeaders = useMemo(
    () => TableService.getHeaderLables(HR_PARTICIPANT_TABLE_FIELDS_CONFIG),
    []
  );
  const onDeleteParticipant = useCallback((e, participantId, index) => {
    e.stopPropagation();
    if (isEmptyValue(localSurveyId)) {
      setLocalParticipants((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    participantCrudFn.onDeleteObject(participantId, () => { hrParticipantsAsync.refetch(); surveyDetailsAsync.refetch(); }
    );
    setIsSurveyChanged(true);
  }, [localSurveyId]);

  const participantTableRows = useMemo(
    () => TableService.getPreparedDataForBaseTable(HR_PARTICIPANT_TABLE_FIELDS_CONFIG, localParticipants, [], undefined, onDeleteParticipant, isReadOnly),
    [localParticipants, onDeleteParticipant, isReadOnly]
  );

  const handleOnCreate = useCallback(() => {
    if (!isEmptyValue(localSurveyId)) return;
    setIsSavingData(true);
    setIsSurveyChanged(true);
    const dataToSend = {
      ...surveyFormValue,
      scores: localScores,
      questions: localQuestions,
      participants: localParticipants,
      copied_survey_id: copiedSurveyId
    };

    delete dataToSend['creator']
    surveyCrudFn.onCreateObject(
      dataToSend,
      (survey) => {
        setIsCopyMode(false)
        setCopiedSurveyId(undefined)
        setLocalSurveyId(survey.id);
        setIsSavingData(false);
      },
      () => setIsSavingData(false)
    );
  }, [surveyFormValue, localScores, localQuestions, localParticipants]);


  const handleSendSurvey = useCallback(() => {

    if (isEmptyValue(surveyFormValue?.hrsurvey_date_expired)) {
      return snackbarAlert.openWarningSnackbarAlert(
        t(`snackbar_alert.hrsurvey_date_expired_is_empty`)
      );
    }
    if (!surveyFormValue?.is_evaluator_exist) {
      return snackbarAlert.openWarningSnackbarAlert(
        t(`snackbar_alert.need_at_least_one_evaluator_must_be_added`)
      );
    }
    if (!surveyFormValue?.is_evaluated_exist) {
      return snackbarAlert.openWarningSnackbarAlert(
        t(`snackbar_alert.one_evaluated_must_be_added`)
      );
    }
    if (localScores.length === 0) {
      return snackbarAlert.openWarningSnackbarAlert(
        t(`snackbar_alert.need_at_least_one_score_must_be_added`)
      );
    }
    if (localQuestions.length === 0) {
      return snackbarAlert.openWarningSnackbarAlert(
        t(`snackbar_alert.need_at_least_one_question_must_be_added`)
      );
    }
    surveyCrudFn.onUpdateObject(localSurveyId, { id: localSurveyId, action: HR_SURVEY_SEND_ACTION }, surveyDetailsAsync.refetch)
  }, [localSurveyId, surveyFormValue, localScores, localQuestions])

  const getDialogTitle = () => {
    if (isCopyMode) return t("dialog.hr_survey_dialog.title_copy");
    if (isEmptyValue(localSurveyId)) return t("dialog.hr_survey_dialog.title_new");
    return t("dialog.hr_survey_dialog.title_edit");
  };

  const onDeleteScore = useCallback((e, scoreId, index) => {
    e.stopPropagation();

    setIsSurveyChanged(true);
    if (isEmptyValue(localSurveyId)) {
      setLocalScores((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    scoreCrudFn.onDeleteObject(scoreId, () =>
      setLocalScores((prev) => prev.filter((score) => score.id !== scoreId))
    );
  }, [localSurveyId]);

  const scoreTableHeaders = useMemo(
    () => TableService.getHeaderLables(SURVEY_SCORE_TABLE_FIELDS_CONFIG),
    []
  );
  const scoreTableRows = useMemo(
    () => TableService.getPreparedDataForBaseTable(SURVEY_SCORE_TABLE_FIELDS_CONFIG, localScores, [], undefined, onDeleteScore, isReadOnly),
    [localScores, onDeleteScore, isReadOnly]
  );

  const onDeleteQuestion = useCallback((e, questionId, index) => {
    e.stopPropagation();
    setIsSurveyChanged(true);
    if (isEmptyValue(localSurveyId)) {
      setLocalQuestions((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    questionCrudFn.onDeleteObject(questionId, () =>
      setLocalQuestions((prev) => prev.filter((question) => question.id !== questionId))
    );
  }, [localSurveyId]);

  const questionTableHeaders = useMemo(
    () => TableService.getHeaderLables(SURVEY_QUESTION_TABLE_FIELDS_CONFIG),
    []
  );
  const questionTableRows = useMemo(
    () => TableService.getPreparedDataForBaseTable(SURVEY_QUESTION_TABLE_FIELDS_CONFIG, localQuestions, [], undefined, onDeleteQuestion, isReadOnly),
    [localQuestions, onDeleteQuestion, isReadOnly]
  );

  const onCloseDialog = useCallback(() => {
    props.onClose(isSurveyChanged)
  }, [isSurveyChanged])



  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      contentAlign="center"
      title={getDialogTitle()}
      allwaysFullScreen={true}
      showTopCloseButton={false}
      showCustomFooter={false}
      showDialogActions={false}
      titleSx={{ height: `${64}px`, marginLeft: `${openMiniDrawerWidth}px` }}
    >
      <IsTestingAppWrapper />
      <FunctionsDrawer
        drawerType={FUNCTION_DRAWER_TYPE_HRS}
        isSavingData={isSavingData || isLoading || surveyCrudFn.isUpdatingLoading}
        surveyId={localSurveyId}
        onCreate={handleOnCreate}
        disableCreateSurvey={isEmptyValue(surveyFormValue?.hrsurvey_name)}
        disableSendSurvey={isReadOnly}
        disableAddAnotherParticipant={!surveyFormValue?.allow_add_another_participant}
        onSend={handleSendSurvey}
        onAddAnotherParticipant={onOpenAnotherParticipantDialog}
        onClose={props.onClose}
      >
        <LoaderWrapper showLoader={filteringDataAsync.loading || isSavingData || surveyCrudFn.isUpdatingLoading}>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={1}
            sx={{ p: 1 }}
          >
            <Grid item xs={8}>
              <BoxWithTypography
                label={t("dialog.hr_survey_dialog.section_basic_data")}
              >
                <LoaderWrapper showLoader={surveyDetailsAsync.loading}>
                  <HrSurveyMainDataForm
                    surveyData={surveyFormValue}
                    filteringData={filteringData}
                    readOnly={isReadOnly}
                    onChange={onChangeSurveyFormTracked}
                    onChangeDate={onChangeSurveyDateTracked}
                    onBlur={handleSurveyFormBlur}
                    expirationMinDate={expirationMinDate}
                  />
                </LoaderWrapper>
              </BoxWithTypography>
            </Grid>

            <Grid item xs={4} md={4}>
              <BoxWithTypography
                label={t("dialog.hr_survey_dialog.section_scores")} >

                <LoaderWrapper showLoader={surveyScoresAsync.loading || scoreCrudFn.isDeletingLoading}>
                  <BaseTable
                    headers={scoreTableHeaders}
                    rows={scoreTableRows}
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 1 }}
                    disabled={isReadOnly}
                    onClick={onOpenScoreDialog}
                  >
                    {t("dialog.hr_survey_dialog.add_score")}
                  </Button>
                </LoaderWrapper>
              </BoxWithTypography>
            </Grid>

            <Grid item xs={12} md={12}>
              <BoxWithTypography
                label={t("dialog.hr_survey_dialog.section_questions")}
              >
                <LoaderWrapper showLoader={surveyQuestionsAsync.loading || questionCrudFn.isDeletingLoading}>
                  <BaseTable
                    headers={questionTableHeaders}
                    rows={questionTableRows}
                    containerStyle={{ maxHeight: "50vh", overflowY: "auto" }}
                    stickyHeader
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 1 }}
                    disabled={isReadOnly}
                    onClick={onOpenQuestionDialog}
                  >
                    {t("dialog.hr_survey_dialog.add_question")}
                  </Button>
                </LoaderWrapper>
              </BoxWithTypography>
            </Grid>

            <Grid item xs={12}>
              <BoxWithTypography
                label={t("dialog.hr_survey_dialog.section_participants")}              >
                <LoaderWrapper showLoader={hrParticipantsAsync.loading || participantCrudFn.isDeletingLoading}>
                  <BaseTable
                    headers={participantTableHeaders}
                    rows={participantTableRows}
                    containerStyle={{ maxHeight: "30vh", overflowY: "auto" }}
                    stickyHeader
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 1 }}
                    disabled={isReadOnly}
                    onClick={onOpenParticipantDialog}
                  >
                    {t("dialog.hr_survey_dialog.add_participant")}
                  </Button>
                </LoaderWrapper>
              </BoxWithTypography>
            </Grid>
          </Grid>
        </LoaderWrapper>
      </FunctionsDrawer>
      {openScoreDialog && (
        <UniversalDetailsDialog
          open={openScoreDialog}
          onClose={onCloseScoreDialog}
          type="survey_score"
          required_fields={["hrscore"]}
          detailsForm={<SurveyScoreDetailsForm
            isNew={isEmptyValue(localSurveyId)}
          />}
          onCreateFn={createSurveyScore}
          saveFn={onSaveData}
          saveLocal={saveLocal}
          onSaveLocal={onSaveLocalScore}
          onRefetchData={surveyScoresAsync.refetch}
          filteringData={filteringData}
        />
      )}
      {openQuestionDialog && (
        <UniversalDetailsDialog
          open={openQuestionDialog}
          onClose={onCloseQuestionDialog}
          type="survey_question"
          required_fields={["hrquestionkind", "hrquestion_name"]}
          detailsForm={<SurveyQuestionDetailsForm
            isNew={isEmptyValue(localSurveyId)}
            questionKinds={localQuestionKinds}
            onOpenNewCategoryDialog={onOpenNewCategoryDialog}

          />}
          onCreateFn={createSurveyQuestion}
          saveFn={onSaveData}
          saveLocal={saveLocal}
          onSaveLocal={onSaveLocalQuestion}
          onRefetchData={surveyQuestionsAsync.refetch}
          filteringData={filteringData}
        />
      )}
      {openParticipantDialog && (
        <UniversalDetailsDialog
          open={openParticipantDialog}
          onClose={onCloseParticipantDialog}
          type="hr_participant"
          required_fields={["hrparticipant", "hrrole"]}
          detailsForm={<HrParticipantDetailsForm
            isNew={isEmptyValue(localSurveyId)}
            userList={userSelectList.value || []}
          />}
          initForm={{ self_evaluated: false }}
          filteringData={filteringData}
          onCreateFn={createHrParticipant}
          saveFn={onSaveData}
          saveLocal={saveLocal}
          onSaveLocal={onSaveLocalParticipant}
          onRefetchData={() => { hrParticipantsAsync.refetch(); surveyDetailsAsync.refetch(); }}
        />
      )}

      {openAnotherParticipantDialog && (
        <UniversalDetailsDialog
          open={openAnotherParticipantDialog}
          onClose={onCloseAnotherParticipantDialog}
          type="hr_participant"
          required_fields={["hrparticipant", "hrrole"]}
          detailsForm={<HrParticipantDetailsForm
            userList={userSelectList.value || []}

          />}
          initForm={{ self_evaluated: false }}
          filteringData={filteringData}
          saveFn={onSaveData}
          saveLocal={true}
          onSaveLocal={onSaveAnotherParticipant}

        />
      )}

      {openNewCategoryDialog && (
        <UniversalDetailsDialog
          open={openNewCategoryDialog}
          onClose={onCloseNewCategoryDialog}
          type="hr_question_kind"
          required_fields={["hrquestion_is_hidden"]}
          detailsForm={<HrQuestionKindForm
          />}
          initForm={{ hrquestion_is_hidden: true, hrsurvey: localSurveyId }}
          onCreateFn={createHrQuestionKind}
          onRefetchData={surveyQuestionKindList.refetch}

        />
      )}
    </BasicDialog>
  );
};

HrSurveyDialog.propTypes = {
  open: PropTypes.bool,
  surveyId: PropTypes.string,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  onSend: PropTypes.func,
};

HrSurveyDialog.defaultProps = {
  open: false,
};

export default HrSurveyDialog;
